import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import DataTable from '../../components/tables/DataTable';
import DialogModal from '../../components/dialog';
import { listHolidays, createHoliday, updateHoliday, deleteHoliday } from '../../utils/queries';
import { formatShipDate } from '../../utils/format';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const weekdayOf = (value) => {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
	if (!match) return '';
	return WEEKDAYS[new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))).getUTCDay()];
};

// Global non-shipping days -- every store's anticipated ship date (order
// date + 3 business days) skips these as well as weekends. Platform admins only.
export default function Holidays() {
	const [holidays, setHolidays] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showPast, setShowPast] = useState(false);
	const [editing, setEditing] = useState(null);
	const [formDate, setFormDate] = useState('');
	const [formName, setFormName] = useState('');
	const [formError, setFormError] = useState('');
	const [saving, setSaving] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState(null);

	const refresh = () => {
		setLoading(true);
		listHolidays().then((result) => {
			if (result?.success) setHolidays(result.data.holidays || []);
			else toast.error(result?.error?.message || 'Could not load holidays.');
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
	}, []);

	const today = new Date().toISOString().slice(0, 10);
	const visible = showPast ? holidays : holidays.filter((h) => h.holiday_date >= today);

	const openForm = (holiday) => {
		setEditing(holiday || 'new');
		setFormDate(holiday?.holiday_date || '');
		setFormName(holiday?.name || '');
		setFormError('');
	};

	const save = async () => {
		if (!formDate || !formName.trim()) {
			setFormError('Date and name are required.');
			return;
		}
		setSaving(true);
		const body = { holidayDate: formDate, name: formName.trim() };
		const result =
			editing === 'new' ? await createHoliday(body) : await updateHoliday(editing.holiday_id, body);
		setSaving(false);
		if (!result?.success) {
			setFormError(result?.error?.message || 'Could not save the holiday.');
			return;
		}
		toast.success(editing === 'new' ? 'Holiday added.' : 'Holiday updated.');
		setEditing(null);
		refresh();
	};

	const confirmDelete = async () => {
		const target = deleteTarget;
		setDeleteTarget(null);
		const result = await deleteHoliday(target.holiday_id);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Could not delete the holiday.');
			return;
		}
		toast.success(`${target.name} removed.`);
		refresh();
	};

	const columns = [
		{ key: 'holiday_date', header: 'Date', render: (value) => formatShipDate(value) },
		{ key: '__weekday', header: 'Day', sortable: false, render: (_v, row) => weekdayOf(row.holiday_date) },
		{ key: 'name', header: 'Name' },
		{
			key: '__actions',
			header: '',
			sortable: false,
			align: 'right',
			render: (_v, row) => (
				<span className='flex justify-end gap-3'>
					<button type='button' className='text-sm text-gray-500 hover:text-black' onClick={() => openForm(row)}>
						Edit
					</button>
					<button type='button' className='text-sm text-pi-red hover:underline' onClick={() => setDeleteTarget(row)}>
						Delete
					</button>
				</span>
			),
		},
	];

	return (
		<div className='py-8 pr-8 max-w-3xl'>
			<div className='flex items-center justify-between mb-2'>
				<h1 className='text-2xl font-light tracking-tight'>Holidays</h1>
				<button type='button' className='btn-primary-small' onClick={() => openForm(null)}>
					Add holiday
				</button>
			</div>
			<p className='text-sm text-gray-500 mb-5'>
				Non-shipping days for every store. Anticipated ship dates are the order date plus 3 business days,
				skipping weekends and these dates (Eastern time). Changes apply to new orders and approvals; existing
				dates aren't recalculated.
			</p>

			<label className='flex items-center gap-2 text-sm text-gray-600 mb-3'>
				<input type='checkbox' checked={showPast} onChange={(e) => setShowPast(e.target.checked)} />
				Show past holidays
			</label>

			<DataTable
				columns={columns}
				data={visible}
				loading={loading}
				emptyHeading='No upcoming holidays'
				emptyMessage='Add the days the plant does not ship.'
			/>

			<DialogModal
				open={Boolean(editing)}
				setOpen={(open) => !open && !saving && setEditing(null)}
				title={editing === 'new' ? 'Add holiday' : 'Edit holiday'}
			>
				<div>
					<label>Date</label>
					<input
						type='date'
						className='form-input w-full'
						value={formDate}
						onChange={(e) => setFormDate(e.target.value)}
					/>
				</div>
				<div>
					<label>Name</label>
					<input
						className='form-input w-full'
						maxLength={100}
						value={formName}
						onChange={(e) => setFormName(e.target.value)}
					/>
				</div>
				{formError && <div className='text-sm text-pi-red'>{formError}</div>}
				<div className='flex gap-3 pt-2'>
					<button type='button' className='btn-primary' disabled={saving} onClick={save}>
						{saving ? 'Saving…' : 'Save'}
					</button>
					<button type='button' className='btn-secondary' disabled={saving} onClick={() => setEditing(null)}>
						Close
					</button>
				</div>
			</DialogModal>

			<DialogModal open={Boolean(deleteTarget)} setOpen={(open) => !open && setDeleteTarget(null)} title='Delete holiday'>
				<p className='text-sm text-gray-600'>
					Remove {deleteTarget?.name} ({formatShipDate(deleteTarget?.holiday_date)})? Orders placed from now on will
					treat it as a business day.
				</p>
				<div className='flex gap-3 pt-2'>
					<button type='button' className='btn-alert' onClick={confirmDelete}>
						Delete
					</button>
					<button type='button' className='btn-secondary' onClick={() => setDeleteTarget(null)}>
						Cancel
					</button>
				</div>
			</DialogModal>
		</div>
	);
}
